import { useState, useCallback } from 'react';
import { useQuery } from 'react-query';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Tooltip, CircularProgress,
  ToggleButton, ToggleButtonGroup, TextField, Button, MenuItem, Select,
  FormControl, InputLabel,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useAuth } from '../../auth/AuthContext';
import { getAdminLogs, listAppContexts } from '../../api/developerApi';
import ApiErrorAlert from '../../components/ApiErrorAlert';

// ---------------------------------------------------------------------------
// API Logs — the account's tenant-wide API activity log.
//
// Reads the owner-gated /developer/logs dev-API route (developerApi.getAdminLogs,
// ADR-0060 / #582): tenant-wide across EVERY app context by default, with an
// optional single-context filter. The confined partner route (/v1/admin/logs)
// could only ever show the one context its bearer was pinned to, so it was
// dropped here in favor of the cross-context owner surface — mirroring the
// admin-app's #574 re-point.
//
// Interaction model: the first query is gated on an explicit "Fetch logs" press
// (CloudWatch Logs Insights is metered and can take seconds), but once a fetch
// has happened a discrete filter pick — env, a preset, the context / resource /
// method selector, or the errors-only toggle — re-queries immediately. Editing
// the From / To pickers drops the active preset and waits for the next Fetch (a
// half-typed datetime never fires a query).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRESETS = [
  { label: '30m', minutes: 30 },
  { label: '1h',  minutes: 60 },
  { label: '6h',  minutes: 360 },
  { label: '24h', minutes: 1440 },
];

// A subset of the backend's accepted resource allow-list (DeveloperLogsHandler
// VALID_RESOURCES, which also permits erasure-requests/export) — the same
// user-facing set the admin-app reference offers. Every value here validates
// server-side; out-of-list values are rejected with 400.
// `entities`/`namespaces` are the ADR-0064 identity surface; `clients`/`orgs`
// stay because historical log rows still carry them (the backend still accepts
// both), so dropping them would hide pre-0.35.0 history.
const RESOURCES = ['', 'documents', 'records', 'search', 'schemas', 'folders',
  'entities', 'namespaces', 'clients', 'orgs', 'users', 'usage', 'auth', 'models',
  'ping', 'rag', 'chat', 'ask'];
const METHODS   = ['', 'GET', 'POST', 'PUT', 'DELETE'];

// Sentinel for the context selector's "All contexts" option — the default,
// tenant-wide view. An empty context sends no context filter to the server.
const ALL_CONTEXTS = '';

// Format a Date as a `datetime-local` value in the user's LOCAL wall-clock.
// NOTE: `date.toISOString()` is UTC — using it here (the old bug) shifted the
// window by the local UTC offset, since the <input type="datetime-local"> value
// is read back as LOCAL time in buildParams (`new Date(window.startTime)`). For
// a negative-offset (e.g. US) user that pushed the whole query window into the
// future, so recent logs fell outside it and the page showed nothing.
export function toLocalInput(date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

// `end` is CEILED to the next whole minute, never floored to "now" (the old
// `new Date()` behavior). `toLocalInput`'s `.slice(0, 16)` necessarily
// truncates to minute precision for the `datetime-local` picker — flooring
// that truncation can put `endTime` *before* activity generated only
// moments earlier, permanently excluding it from the query window (the same
// bug fixed in admin-app's LogsPage.tsx `windowOfLastMinutes` — see its
// comment for the full mechanism, confirmed live against staging there).
// Ceiling keeps `endTime` always >= the instant this function was called.
function defaultWindow(minutes = 60) {
  const MINUTE_MS = 60_000;
  const end   = new Date(Math.ceil(Date.now() / MINUTE_MS) * MINUTE_MS);
  const start = new Date(end.getTime() - minutes * 60 * 1000);
  return { startTime: toLocalInput(start), endTime: toLocalInput(end) };
}

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

function StatusChip({ status }) {
  if (!status) return null;
  const color = status >= 500 ? 'error' : status >= 400 ? 'warning' : 'success';
  return <Chip label={status} size="small" color={color} sx={{ fontSize: '0.7rem', height: 20 }} />;
}

// ---------------------------------------------------------------------------
// Method chip
// ---------------------------------------------------------------------------

const METHOD_COLORS = {
  GET:    { bg: '#e3f2fd', color: '#1565c0' },
  POST:   { bg: '#e8f5e9', color: '#2e7d32' },
  PUT:    { bg: '#fff8e1', color: '#f57f17' },
  DELETE: { bg: '#fce4ec', color: '#c62828' },
};

// #453: the "why" for a failed call. Until now a 4xx/5xx row showed only that it
// failed, never why. The backend emits two fields on failure lines — `errorCode`
// (present only when the failure had a typed code) and `requestId` (the correlation
// id to quote to support). Both are absent on successes AND on any line recorded
// before #453 that is still inside the retention window, so "no detail" is the
// normal case for older rows and must read as blank, not as an error.
//
// Deliberately NOT shown: the error message. Request and response bodies are never
// logged (they can carry customer data), so the message never reaches CloudWatch —
// there is no more detail to surface here, by design.
function FailureDetail({ status, errorCode, requestId }) {
  if (!status || status < 400) return <span>—</span>;
  if (!errorCode && !requestId) {
    return (
      <Tooltip title="No detail recorded for this call — it predates error-detail logging.">
        <span
          aria-label="No detail recorded — this call predates error-detail logging."
          style={{ color: 'inherit', opacity: 0.6 }}
        >—</span>
      </Tooltip>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
      {errorCode && (
        <Chip
          label={errorCode}
          size="small"
          color={status >= 500 ? 'error' : 'warning'}
          variant="outlined"
          sx={{ fontSize: '0.65rem', height: 18, maxWidth: '100%' }}
        />
      )}
      {requestId && (
        <Tooltip title={`Request ID ${requestId} — quote this when contacting support.`}>
          <span style={{
            fontFamily: 'monospace', fontSize: '0.65rem', opacity: 0.75,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            cursor: 'help',
          }}>{requestId}</span>
        </Tooltip>
      )}
    </Box>
  );
}

function MethodChip({ method }) {
  const cfg = METHOD_COLORS[method] || { bg: '#f5f5f5', color: '#616161' };
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
      fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
      bgcolor: cfg.bg, color: cfg.color,
    }}>
      {method || '—'}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Resource chip
// ---------------------------------------------------------------------------

function ResourceChip({ resource }) {
  if (!resource || resource === 'null') return <span style={{ color: '#9e9e9e' }}>—</span>;
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
      fontSize: '0.7rem', fontWeight: 600,
      bgcolor: '#f3e5f5', color: '#6a1b9a',
    }}>
      {resource}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ApiLogsPage() {
  const { user } = useAuth();
  const [env, setEnv]             = useState('test');
  const [preset, setPreset]       = useState(60); // active time-horizon (minutes), or null = custom
  const [window, setWindow]       = useState(defaultWindow(60));
  const [resource, setResource]   = useState('');
  const [method, setMethod]       = useState('');
  const [contextId, setContextId] = useState(ALL_CONTEXTS);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [queryKey, setQueryKey]   = useState(null); // null = not yet fetched

  // The env selector picks which tenant (live/test) the OWNER-gated route
  // resolves — the client sends only `tenant=<env>`; the tenantId itself is
  // derived server-side from the owner's session (never sent). We still read the
  // env's tenantId locally to disable Fetch when that env has no tenant yet.
  const envTenantId = (e) => (e === 'live' ? user?.liveTenantId : user?.testTenantId);
  const tenantId = envTenantId(env);

  // The account's app contexts for the selected env, to populate the context
  // filter. Owner-gated developer-API list (the only surface that can enumerate
  // every context — the partner bearer is confined to a single context).
  // Best-effort: if it fails the filter stays on "All contexts" and logs still work.
  const { data: contexts = [], isLoading: contextsLoading } = useQuery(
    ['devAppContexts', env],
    () => listAppContexts(env),
    { enabled: !!user },
  );

  const buildParams = useCallback(() => {
    // A relative preset ("last N minutes") means "ending now" at the ACTUAL
    // moment of every fetch, not whatever instant it happened to be picked
    // at — recompute the window fresh here rather than trusting whatever
    // start/end strings are frozen into `window` state. Without this,
    // clicking "Fetch logs" again (this page's Refresh-equivalent) would
    // keep re-querying the same, increasingly stale window forever — the
    // ceiling in `defaultWindow` only bounds the gap from the instant it was
    // last CALLED, so it has to be called again on every fetch (mirrors
    // admin-app's LogsPage.tsx queryFn fix for the identical bug). A custom
    // (`preset == null`) range is the user's own explicit choice and is
    // used exactly as entered.
    const effectiveWindow = preset != null ? defaultWindow(preset) : window;
    return {
      startTime: new Date(effectiveWindow.startTime).toISOString(),
      endTime:   new Date(effectiveWindow.endTime).toISOString(),
      ...(resource   && { resource }),
      ...(method     && { method }),
      ...(contextId  && { contextId }),
      ...(errorsOnly && { errorsOnly: true }),
      limit: 200,
    };
  }, [preset, window, resource, method, contextId, errorsOnly]);

  const { data, isFetching, error } = useQuery(
    ['api-logs', queryKey],
    // Owner-gated, tenant-wide read via the developer API: no contextId returns
    // activity across EVERY context; a selected context narrows to it. `env`
    // selects the tenant (?tenant=live|test). Restores the tenant-wide surface
    // lost when /developer/logs/api migrated to the confined /v1/admin/logs.
    () => getAdminLogs(env, buildParams()),
    {
      enabled: queryKey !== null,
      keepPreviousData: true,
      staleTime: 0,
    }
  );

  const handleFetch = () => {
    // Force a new query key so re-clicking always re-fetches
    setQueryKey(Date.now());
  };

  // Re-run the query immediately so a discrete filter click (errors-only, a
  // resource/method/context pick, an env switch, a preset) applies without a
  // second "Fetch logs" press — but only once an initial fetch has happened, so
  // we never fire an (expensive) CWL Insights query before the user asks for one.
  // The new param is read by buildParams on the re-render this triggers. Gated
  // on tenantId too: an env with no provisioned tenant has nothing to query, so
  // don't fire a request the server would answer with 500 "Tenant not configured"
  // (matches the Fetch button's own `!tenantId` guard).
  const refetchIfActive = () => {
    if (queryKey !== null && tenantId) setQueryKey(Date.now());
  };

  // An env switch changes which tenant's contexts exist — a context selected in
  // one env may not exist in the other. Reset to "All contexts" so we never send
  // a contextId that doesn't belong to the new env, then re-query. Guard on the
  // NEW env's tenant (state hasn't updated yet, so read it directly) so switching
  // to an unprovisioned env doesn't fire a doomed query.
  const handleEnvChange = (_, v) => {
    if (!v) return;
    setEnv(v);
    setContextId(ALL_CONTEXTS);
    if (queryKey !== null && envTenantId(v)) setQueryKey(Date.now());
  };

  const applyPreset = (minutes) => {
    setPreset(minutes);
    setWindow(defaultWindow(minutes));
    refetchIfActive();
  };

  // A manual From/To edit means we're no longer on a fixed horizon.
  const setWindowField = (field) => (e) => {
    const value = e.target.value;
    setPreset(null);
    setWindow((w) => ({ ...w, [field]: value }));
  };

  const presetLabel = preset != null
    ? `last ${PRESETS.find((p) => p.minutes === preset)?.label}`
    : 'custom range';

  // Keep the Select on a valid value: the selected context, if it's "All" or
  // still present in the loaded list; otherwise fall back to "All contexts".
  const contextValue =
    contextId === ALL_CONTEXTS || contexts.some((c) => c.contextId === contextId)
      ? contextId
      : ALL_CONTEXTS;

  const entries = data?.entries || [];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>API Logs</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Recent API activity across all of your app contexts. Only request
        metadata is recorded — method, path, status, latency, key id, and
        context — never request or response bodies. Failed calls also show a
        Detail column with the error code and a request ID to quote to support.
      </Typography>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexDirection="column" gap={2}>

          {/* Env + context + time presets */}
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup exclusive size="small" value={env}
              onChange={handleEnvChange}>
              <ToggleButton value="test" sx={{ fontSize: '0.8rem', px: 2 }}>Test</ToggleButton>
              <ToggleButton value="live" sx={{ fontSize: '0.8rem', px: 2 }}>Live</ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="logs-context-label">Context</InputLabel>
              <Select
                labelId="logs-context-label"
                label="Context"
                value={contextValue}
                disabled={contextsLoading}
                onChange={(e) => { setContextId(e.target.value); refetchIfActive(); }}
              >
                <MenuItem value={ALL_CONTEXTS}><em>All contexts</em></MenuItem>
                {contexts.map((c) => (
                  <MenuItem key={c.contextId} value={c.contextId}>
                    {c.name ? `${c.contextId} — ${c.name}` : c.contextId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButtonGroup exclusive size="small" value={preset}
              onChange={(_, v) => { if (v != null) applyPreset(v); }}>
              {PRESETS.map(p => (
                <ToggleButton key={p.label} value={p.minutes}
                  sx={{ minWidth: 0, px: 1.5, fontSize: '0.75rem' }}>
                  {p.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <TextField size="small" type="datetime-local" label="From"
              value={window.startTime}
              onChange={setWindowField('startTime')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 240 } }} />
            <TextField size="small" type="datetime-local" label="To"
              value={window.endTime}
              onChange={setWindowField('endTime')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 240 } }} />

            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Showing <strong>{presetLabel}</strong>
            </Typography>
          </Box>

          {/* Filters */}
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="logs-resource-label">Resource</InputLabel>
              <Select labelId="logs-resource-label" value={resource} label="Resource"
                onChange={e => { setResource(e.target.value); refetchIfActive(); }}>
                {RESOURCES.map(r => (
                  <MenuItem key={r} value={r}>{r || 'All resources'}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="logs-method-label">Method</InputLabel>
              <Select labelId="logs-method-label" value={method} label="Method"
                onChange={e => { setMethod(e.target.value); refetchIfActive(); }}>
                {METHODS.map(m => (
                  <MenuItem key={m} value={m}>{m || 'All methods'}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButton size="small" value="errors" selected={errorsOnly}
              onChange={() => { setErrorsOnly(v => !v); refetchIfActive(); }}
              sx={{ fontSize: '0.75rem', px: 1.5, color: errorsOnly ? 'error.main' : undefined }}>
              Errors only
            </ToggleButton>

            <Button variant="contained" size="small" onClick={handleFetch}
              disabled={isFetching || !tenantId}
              startIcon={isFetching ? <CircularProgress size={14} color="inherit" /> : <Refresh />}>
              {isFetching ? 'Querying…' : 'Fetch logs'}
            </Button>

            {data && !isFetching && (
              <Typography variant="caption" color="text.secondary">
                {entries.length}{data.truncated ? ' (truncated at 200)' : ''} entries
                {' · '}{data.queryDurationMs}ms
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Results */}
      <ApiErrorAlert error={error} sx={{ mb: 2 }} />

      {isFetching && entries.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isFetching && data && entries.length === 0 && (
        <Typography color="text.secondary">No log entries found in this time window.</Typography>
      )}

      {entries.length > 0 && (
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, width: 160 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 80 }}>Method</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 100 }}>Resource</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 120 }}>Context</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Path</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 80 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 90 }}>Latency</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 140 }}>
                    <Tooltip title="The credential that made the call. Prefixes: sk_ = root API key, ssk_ = scoped API key, st_ = short-lived scoped token.">
                      <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>Key ID</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 170 }}>
                    <Tooltip title="Why a failed call was rejected: the error code (when the failure had one) and the request ID to quote to support. Request and response bodies are never logged, so no further detail is available.">
                      <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>Detail</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e, i) => (
                  <TableRow key={i} sx={{
                    bgcolor: e.status >= 500 ? 'error.50'
                           : e.status >= 400 ? 'warning.50'
                           : 'inherit',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem',
                      color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {e.timestamp ? new Date(e.timestamp).toLocaleTimeString([], {
                        hour: '2-digit', minute: '2-digit', second: '2-digit',
                        fractionalSecondDigits: 3,
                      }) : '—'}
                    </TableCell>
                    <TableCell><MethodChip method={e.method} /></TableCell>
                    <TableCell><ResourceChip resource={e.resource} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      color: 'text.secondary' }}>
                      <Tooltip title={e.contextId || ''}>
                        <span>{e.contextId && e.contextId !== 'null' ? e.contextId : '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={e.path || ''}>
                        <span>{e.path || '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell><StatusChip status={e.status} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem',
                      color: e.durationMs > 5000 ? 'error.main'
                           : e.durationMs > 2000 ? 'warning.main' : 'text.secondary' }}>
                      {e.durationMs != null ? `${e.durationMs}ms` : '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      color: 'text.secondary' }}>
                      <Tooltip title={e.keyId || ''}>
                        <span>{e.keyId || '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', overflow: 'hidden' }}>
                      <FailureDetail
                        status={e.status}
                        errorCode={e.errorCode}
                        requestId={e.requestId}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {data.truncated && (
            <Typography variant="caption" color="warning.main" sx={{ p: 1.5, display: 'block' }}>
              Results truncated at 200 — narrow the time window or add filters to see all logs.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
